import "./instrument"; // MUST be first for forensic tracing
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import { BrowserRouter } from "react-router-dom";
import { UIProvider } from "./context/UIContext";

// Auto-recover from stale dynamic imports / cached bundles after a new deployment
window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});

const RootApp = Sentry.withProfiler(App);

interface RootContainer extends HTMLElement {
  _reactRoot?: ReturnType<typeof ReactDOM.createRoot>;
}

const rootElement = document.getElementById("root") as RootContainer;
if (!rootElement) throw new Error("Failed to find the root element");

// HMR-safe singleton root management: Store the root on the DOM element
// itself to prevent "container already passed to createRoot" during dev.
let root = rootElement._reactRoot;
if (!root) {
  root = ReactDOM.createRoot(rootElement);
  rootElement._reactRoot = root;
}

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8 font-sans">
          <div className="max-w-md w-full text-center bg-white rounded-[10px] p-10 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-100">
            <h1 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">
              Oops! Something went wrong
            </h1>
            <p className="text-base text-slate-500 mb-8 leading-relaxed">
              We've encountered an error. Our team has been notified.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white font-bold py-3 px-6 rounded-[10px] w-full"
            >
              Refresh Page
            </button>
            <p className="mt-4 text-xs text-slate-400 font-mono italic">
              {error instanceof Error ? error.message : "No message provided"}
            </p>
          </div>
        </div>
      )}
      showDialog
    >
      <UIProvider>
        <BrowserRouter>
          <RootApp />
        </BrowserRouter>
      </UIProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
